import { PayrollRun, WorkLog } from "@wingspanhq/wingspan-api-sdk";

import { PAYROLL_RUNS_OPEN_STATUSES } from "../../PayrollRuns/constants";
import { isStatusInReview } from "../utils/workLogPermissions";

/**
 * Whether a submitted work log can no longer reach a payroll run for its own pay
 * period — the design's "Approval expired" terminal-ish state.
 *
 * Approving is only meaningful while a run for the period can still absorb the
 * work log. Three ways that stops being true:
 *
 *  - the run's `approvalDeadline` has passed;
 *  - the run has moved past the statuses that still collect work logs (see
 *    {@link PAYROLL_RUNS_OPEN_STATUSES} — a finalized/paid run has its amounts
 *    locked in);
 *  - there is no run for the period at all and the period itself has ended, so
 *    nothing is coming that this could join.
 *
 * In every case the only way to pay the work log is an off-cycle run.
 *
 * Returns `false` while the run is still resolving, so the page never flashes a
 * red "expired" banner at a work log that is merely loading. Callers must pass
 * `payrollRun: undefined` **only** once the lookup has settled.
 */
export const selectorWorkLogApprovalExpired = ({
  workLog,
  payrollRun,
  now = new Date()
}: {
  workLog: WorkLog | undefined;
  payrollRun: PayrollRun | undefined;
  now?: Date;
}): boolean => {
  // Only a work log awaiting the payer's decision can expire. Anything already
  // approved, rejected or back with the contractor has left this state.
  if (!isStatusInReview(workLog?.status)) {
    return false;
  }

  // A contractor work log carries no pay period, so it is not tied to a payroll
  // run and never expires.
  if (!workLog?.payPeriodId) {
    return false;
  }

  if (!payrollRun) {
    const periodEnd = workLog.periodEnd;
    return !!periodEnd && periodEnd.getTime() < now.getTime();
  }

  const deadline = payrollRun.approvalDeadline ?? undefined;

  if (deadline && deadline.getTime() <= now.getTime()) {
    return true;
  }

  return (
    !!payrollRun.status &&
    !PAYROLL_RUNS_OPEN_STATUSES.includes(payrollRun.status)
  );
};
